import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { Workbook, Worksheet } from 'exceljs';
import * as fs from 'file-saver';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  finalize,
  forkJoin,
  Observable,
  of,
  startWith,
  Subscription,
} from 'rxjs';
import {
  BudgetReportRow,
  HrAlertService,
  HrHttpService,
  MrfCustomizeFilterField,
  MrfLocationFilters,
} from '../hr-http.service';
import { BUDGET_FORM_ENABLED, canAccessBudgetForm } from '../mrf-view-options';

type BudgetLocationFilterField = Extract<
  MrfCustomizeFilterField,
  'city' | 'zone' | 'subZone' | 'campus' | 'deanName'
>;

const LOCATION_FILTER_ORDER: BudgetLocationFilterField[] = [
  'city',
  'zone',
  'subZone',
  'campus',
  'deanName',
];

const BUDGET_REPORT_FILTER_STORAGE_KEY = 'mrf.budgetReport.filterState';

interface BudgetReportPersistedState {
  selectedCity: Record<string, unknown> | null;
  selectedZone: Record<string, unknown> | null;
  selectedSubZone: Record<string, unknown> | null;
  selectedCampus: Record<string, unknown> | null;
  selectedDean: Record<string, unknown> | null;
  selectedEmpType: Record<string, unknown> | null;
  selectedTeachingType: Record<string, unknown> | null;
  selectedDepartment: Record<string, unknown> | null;
  selectedDesignation: Record<string, unknown> | null;
  selectedBudget: Record<string, unknown> | null;
  showMoreFilters: boolean;
  locationFilters: MrfLocationFilters;
  listLoaded: boolean;
}

@Component({
  selector: 'app-budget-report',
  templateUrl: './budget-report.component.html',
  styleUrls: ['./budget-report.component.scss'],
})
export class BudgetReportComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly icons = {
    filterBudget: 'assets/image/budget-report/filter-budget.svg',
    city: 'assets/image/budget-report/city.svg',
    zone: 'assets/image/budget-report/zone.svg',
    subZone: 'assets/image/budget-report/sub-zone.svg',
    campus: 'assets/image/budget-report/campus.svg',
    deanName: 'assets/image/budget-report/dean-name.svg',
    employeeType: 'assets/image/budget-report/employee-type.svg',
    teachingType: 'assets/image/budget-report/teaching-type.svg',
    department: 'assets/image/budget-report/department.svg',
    designation: 'assets/image/budget-report/designation.svg',
    budget: 'assets/image/budget-report/budget.svg',
    budgetList: 'assets/image/budget-report/budget-list.svg',
    totalBudgetWallet: 'assets/image/budget-report/total-budget-wallet.svg',
  } as const;

  citySearch = new FormControl('');
  zoneSearch = new FormControl('');
  subZoneSearch = new FormControl('');
  campusSearch = new FormControl('');
  deanSearch = new FormControl('');
  empTypeSearch = new FormControl('');
  teachingTypeSearch = new FormControl('');
  departmentSearch = new FormControl('');
  designationSearch = new FormControl('');
  budgetSearch = new FormControl('');

  selectedCity: Record<string, unknown> | null = null;
  selectedZone: Record<string, unknown> | null = null;
  selectedSubZone: Record<string, unknown> | null = null;
  selectedCampus: Record<string, unknown> | null = null;
  selectedDean: Record<string, unknown> | null = null;
  selectedEmpType: Record<string, unknown> | null = null;
  selectedTeachingType: Record<string, unknown> | null = null;
  selectedDepartment: Record<string, unknown> | null = null;
  selectedDesignation: Record<string, unknown> | null = null;
  selectedBudget: Record<string, unknown> | null = null;

  cityOptionsList: Record<string, unknown>[] = [];
  zoneOptionsList: Record<string, unknown>[] = [];
  subZoneOptionsList: Record<string, unknown>[] = [];
  campusOptionsList: Record<string, unknown>[] = [];
  deanOptionsList: Record<string, unknown>[] = [];
  empTypeOptionsList: Record<string, unknown>[] = [];
  teachingTypeOptionsList: Record<string, unknown>[] = [];
  departmentOptionsList: Record<string, unknown>[] = [];
  designationOptionsList: Record<string, unknown>[] = [];
  budgetOptionsList: Record<string, unknown>[] = [
    { label: '2024-25', value: '2024-25' },
    { label: '2025-26', value: '2025-26' },
    { label: '2026-27', value: '2026-27' },
  ];

  cityOptions = of<Record<string, unknown>[]>([]);
  zoneOptions = of<Record<string, unknown>[]>([]);
  subZoneOptions = of<Record<string, unknown>[]>([]);
  campusOptions = of<Record<string, unknown>[]>([]);
  deanOptions = of<Record<string, unknown>[]>([]);
  empTypeOptions = of<Record<string, unknown>[]>([]);
  teachingTypeOptions = of<Record<string, unknown>[]>([]);
  departmentOptions = of<Record<string, unknown>[]>([]);
  designationOptions = of<Record<string, unknown>[]>([]);
  budgetOptions = of<Record<string, unknown>[]>([]);

  locationFilters: MrfLocationFilters = {};
  showMoreFilters = false;
  expandedFiltersLoaded = false;
  listLoaded = false;
  loading = false;
  expandedFiltersLoading = false;
  teachingTypeLoading = false;
  quickFilter = '';
  totalBudget: number | null = null;
  rowData: BudgetReportRow[] = [];
  displayRowData: BudgetReportRow[] = [];
  hasBudgetAccess = false;
  readonly isBudgetFormEnabled = BUDGET_FORM_ENABLED;

  private gridApi?: GridApi<BudgetReportRow>;
  private readonly listsVersion$ = new BehaviorSubject(0);
  private readonly subs = new Subscription();
  private selectPanelSubs = new Subscription();
  private pendingApiCount = 0;

  readonly locationFieldLoading: Record<BudgetLocationFilterField, boolean> = {
    city: false,
    zone: false,
    subZone: false,
    campus: false,
    deanName: false,
  };

  @ViewChildren(MatSelect) private matSelects?: QueryList<MatSelect>;

  readonly overlayNoRowsTemplate =
    '<span class="ag-overlay-no-rows-center">No budget records found for the selected filters.</span>';

  columnDefs: ColDef<BudgetReportRow>[] = [
    { field: 'city', headerName: 'City', minWidth: 110 },
    { field: 'zone', headerName: 'Zone', minWidth: 110 },
    { field: 'subZone', headerName: 'Sub Zone', minWidth: 110 },
    { field: 'campus', headerName: 'Campus', minWidth: 120 },
    { field: 'deanName', headerName: 'Dean Name', minWidth: 130 },
    { field: 'empType', headerName: 'Employee Type', minWidth: 120 },
    { field: 'teachingType', headerName: 'Teaching Type', minWidth: 120 },
    { field: 'department', headerName: 'Department', minWidth: 130 },
    { field: 'designation', headerName: 'Designation', minWidth: 130 },
    {
      field: 'subjectName',
      headerName: 'Subject',
      minWidth: 120,
      valueFormatter: (params) => {
        const value = String(params.value ?? '').trim();
        return value && value !== '—' ? value : '—';
      },
    },
    {
      field: 'budgetAmount',
      headerName: 'Budget',
      minWidth: 120,
      cellClass: 'brpt__budget-cell',
      valueFormatter: (params) => this.formatBudget(params.value as number | null),
    },
    {
      colId: 'action',
      field: 'budgetId',
      headerName: 'Action',
      minWidth: 90,
      maxWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<BudgetReportRow>) =>
        this.buildEditCell(params.data),
      onCellClicked: (params: { data?: BudgetReportRow }) => this.onEditRow(params.data),
      suppressNavigable: true,
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    filter: false,
    resizable: true,
    suppressHeaderMenuButton: true,
    menuTabs: [],
  };

  constructor(
    private hrHttp: HrHttpService,
    private hrAlert: HrAlertService,
    private spinner: NgxSpinnerService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.hrHttp.getUserRoles().pipe(catchError(() => of(null))).subscribe((roles) => {
        const permissions = roles
          ? this.hrHttp.setMrfHrmsPermissionsFromResponse(roles)
          : this.hrHttp.getMrfHrmsPermissions();
        this.hasBudgetAccess = canAccessBudgetForm(permissions);
        if (!this.hasBudgetAccess) {
          this.hrAlert.show('No permission to access budget module.', 'Warning');
          void this.router.navigate(['/hrDetails/mrfDashboard']);
          return;
        }
        this.initFilteredOptions();
        const savedState = this.readPersistedFilterState();
        if (savedState) {
          this.restoreFilterState(savedState);
        } else {
          this.loadInitialLocationFilters();
          this.loadExpandedFilterMasters();
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.bindSelectPanelSync();
    this.subs.add(
      this.matSelects?.changes.subscribe(() => this.bindSelectPanelSync()) ?? of(null).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.selectPanelSubs.unsubscribe();
    this.subs.unsubscribe();
    this.pendingApiCount = 0;
    this.spinner.hide();
  }

  get recordCountLabel(): string {
    const count = this.displayRowData.length;
    return `${count} record${count === 1 ? '' : 's'} found`;
  }

  get totalBudgetLabel(): string {
    if (this.totalBudget == null || Number.isNaN(this.totalBudget)) {
      return '—';
    }
    return `₹ ${this.hrHttp.formatMrfBudgetAmount(this.totalBudget)}`;
  }

  get isApiLoading(): boolean {
    return this.pendingApiCount > 0;
  }

  isLocationFieldLoading(field: BudgetLocationFilterField): boolean {
    return this.locationFieldLoading[field];
  }

  onGridReady(event: GridReadyEvent<BudgetReportRow>): void {
    this.gridApi = event.api;
  }

  onQuickFilterChanged(event: Event): void {
    this.quickFilter = String((event.target as HTMLInputElement)?.value ?? '');
    this.syncDisplayedRows();
  }

  applyFilter(): void {
    const campusId = this.readId(this.selectedCampus, ['campusId', 'id']);
    if (campusId == null) {
      this.hrAlert.show('Please select Campus.', 'Warning');
      return;
    }

    const filters: MrfLocationFilters = {
      cityId: this.readId(this.selectedCity, ['cityId', 'id']),
      zoneName: this.readName(this.selectedZone, ['zoneName', 'name', 'zone']),
      subZone: this.readName(this.selectedSubZone, ['subZone', 'name', 'subZoneName']),
      campusId,
      deanName: this.readName(this.selectedDean, ['deanName', 'name', 'dean']),
    };
    this.locationFilters = filters;
    this.persistFilterState(true);
    this.loadBudgetList(filters);
  }

  clearFilters(): void {
    if (this.loading || this.isApiLoading) {
      return;
    }

    try {
      sessionStorage.removeItem(BUDGET_REPORT_FILTER_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }

    this.selectedCity = null;
    this.selectedZone = null;
    this.selectedSubZone = null;
    this.selectedCampus = null;
    this.selectedDean = null;
    this.selectedEmpType = null;
    this.selectedTeachingType = null;
    this.selectedDepartment = null;
    this.selectedDesignation = null;
    this.selectedBudget = null;
    this.locationFilters = {};
    this.showMoreFilters = false;
    this.quickFilter = '';
    this.rowData = [];
    this.displayRowData = [];
    this.totalBudget = null;
    this.listLoaded = false;

    this.citySearch.setValue('');
    this.zoneSearch.setValue('');
    this.subZoneSearch.setValue('');
    this.campusSearch.setValue('');
    this.deanSearch.setValue('');
    this.empTypeSearch.setValue('');
    this.teachingTypeSearch.setValue('');
    this.departmentSearch.setValue('');
    this.designationSearch.setValue('');
    this.budgetSearch.setValue('');

    this.departmentOptionsList = [];
    this.designationOptionsList = [];
    LOCATION_FILTER_ORDER.forEach((field) => {
      this.setLocationOptionsList(field, []);
      this.locationFieldLoading[field] = false;
    });
    this.listsVersion$.next(this.listsVersion$.value + 1);
    this.loadInitialLocationFilters();
  }

  toggleViewMoreFilters(): void {
    this.showMoreFilters = !this.showMoreFilters;
    if (this.showMoreFilters && !this.expandedFiltersLoaded) {
      this.loadExpandedFilterMasters();
    }
    setTimeout(() => this.bindSelectPanelSync());
  }

  createBudget(): void {
    if (!BUDGET_FORM_ENABLED) {
      return;
    }
    if (!this.hasBudgetAccess) {
      this.hrAlert.show('No permission to create budget.', 'Warning');
      return;
    }
    this.persistFilterState();
    void this.router.navigate(['/hrDetails/budgetDetails']);
  }

  exportReport(): void {
    const exportColumns = this.columnDefs.filter((col) => (col.colId || col.field) !== 'action');
    const rows: BudgetReportRow[] = [];
    if (this.gridApi) {
      this.gridApi.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) {
          rows.push(node.data);
        }
      });
    } else {
      rows.push(...this.displayRowData);
    }
    if (!exportColumns.length || !rows.length) {
      this.hrAlert.show('No data available to export.', 'Warning');
      return;
    }
    void this.downloadBudgetExcel(exportColumns, rows);
  }

  private async downloadBudgetExcel(
    exportColumns: ColDef<BudgetReportRow>[],
    rows: BudgetReportRow[]
  ): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Budget Report');
    const lastColLetter = this.excelColumnLetter(Math.max(exportColumns.length, 3));
    const now = new Date();
    const reportDateTime =
      now.toLocaleDateString('en-GB') + ' ' + now.toLocaleTimeString('en-GB');

    worksheet.mergeCells('A1:B4');
    worksheet.mergeCells(`C1:${lastColLetter}1`);
    worksheet.mergeCells(`C2:${lastColLetter}2`);
    worksheet.mergeCells(`C3:${lastColLetter}3`);
    worksheet.mergeCells(`C4:${lastColLetter}4`);

    [1, 2, 3, 4].forEach((rowNumber) => {
      worksheet.getRow(rowNumber).height = 18;
    });

    await this.addBudgetExcelLogo(workbook, worksheet);

    worksheet.getCell('C1').value = 'Sri Chaitanya Educational Institutions';
    worksheet.getCell('C2').value = 'Budget Report';
    worksheet.getCell('C3').value = `Records : ${rows.length}`;
    worksheet.getCell('C4').value = `Date : ${reportDateTime}`;

    ['C1', 'C2', 'C3', 'C4'].forEach((cell) => {
      worksheet.getCell(cell).alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
    });

    worksheet.getCell('C1').font = {
      bold: true,
      size: 16,
      underline: true,
      color: { argb: 'FF8C00' },
    };
    worksheet.getCell('C2').font = { bold: true, size: 13 };
    worksheet.getCell('C3').font = { bold: true, size: 12 };
    worksheet.getCell('C4').font = { bold: true, size: 12 };

    worksheet.addRow([]);
    worksheet.addRow([]);

    const headers = exportColumns.map((col) => String(col.headerName ?? col.field ?? ''));
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 22;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: false };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
        bottom: { style: 'thin' },
      };
    });

    rows.forEach((row) => {
      const dataRow = worksheet.addRow(exportColumns.map((col) => this.excelCellValue(col, row)));
      dataRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
          bottom: { style: 'thin' },
        };
        const field = String(exportColumns[colNumber - 1]?.field ?? '');
        cell.alignment = {
          horizontal: field === 'budgetAmount' ? 'right' : 'left',
          vertical: 'middle',
          wrapText: false,
        };
      });
    });

    this.fitBudgetExcelColumns(worksheet, exportColumns.length, headerRow.number);
    worksheet.getColumn(1).width = Math.max(Number(worksheet.getColumn(1).width ?? 0), 14);
    worksheet.getColumn(2).width = Math.max(Number(worksheet.getColumn(2).width ?? 0), 14);

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        fs.saveAs(blob, `BudgetReport_${now.toISOString().slice(0, 10)}.xlsx`);
        this.hrAlert.show('Excel downloaded successfully.', 'Success');
      })
      .catch((error) => {
        console.error(error);
        this.hrAlert.show('Failed to download Excel file.', 'Error');
      });
  }

  private excelCellValue(col: ColDef<BudgetReportRow>, row: BudgetReportRow): string | number {
    const field = String(col.field ?? '');
    if (field === 'budgetAmount') {
      return this.formatBudget(row.budgetAmount);
    }
    const value = (row as unknown as Record<string, unknown>)[field];
    if (value == null || String(value).trim() === '') {
      return '';
    }
    return String(value);
  }

  private excelColumnLetter(columnNumber: number): string {
    let n = columnNumber;
    let letter = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      letter = String.fromCharCode(65 + rem) + letter;
      n = Math.floor((n - 1) / 26);
    }
    return letter || 'A';
  }

  private fitBudgetExcelColumns(
    worksheet: Worksheet,
    columnCount: number,
    tableStartRow: number
  ): void {
    for (let index = 1; index <= columnCount; index++) {
      const column = worksheet.getColumn(index);
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
        if (rowNumber < tableStartRow) {
          return;
        }
        const text = cell.value == null ? '' : String(cell.value);
        maxLength = Math.max(maxLength, text.length);
      });
      const minWidth = index === 1 ? 18 : 12;
      column.width = Math.min(50, Math.max(minWidth, maxLength + 3));
    }
  }

  private async addBudgetExcelLogo(workbook: Workbook, worksheet: Worksheet): Promise<void> {
    try {
      const response = await fetch('assets/image/budget-report/excel-logo.png');
      if (!response.ok) {
        return;
      }
      const buffer = await response.arrayBuffer();
      const logoId = workbook.addImage({
        buffer,
        extension: 'png',
      });
      worksheet.addImage(logoId, {
        tl: { col: 0, row: 0 },
        br: { col: 2, row: 4 },
        editAs: 'oneCell',
      } as Parameters<Worksheet['addImage']>[1]);
    } catch {
      /* logo is optional */
    }
  }

  onCityChange(value: Record<string, unknown> | null): void {
    if (!this.hasSelectionChanged(this.selectedCity, value, this.compareByCity)) {
      return;
    }
    this.selectedCity = value;
    this.clearLocationDependents('city');
    this.reloadDownstreamLocationFields('city');
  }

  onZoneChange(value: Record<string, unknown> | null): void {
    if (!this.hasSelectionChanged(this.selectedZone, value, this.compareByZone)) {
      return;
    }
    this.selectedZone = value;
    this.clearLocationDependents('zone');
    this.reloadDownstreamLocationFields('zone');
  }

  onSubZoneChange(value: Record<string, unknown> | null): void {
    if (!this.hasSelectionChanged(this.selectedSubZone, value, this.compareBySubZone)) {
      return;
    }
    this.selectedSubZone = value;
    this.clearLocationDependents('subZone');
    this.reloadDownstreamLocationFields('subZone');
  }

  onDeanChange(value: Record<string, unknown> | null): void {
    if (!this.hasSelectionChanged(this.selectedDean, value, this.compareByDean)) {
      return;
    }
    this.selectedDean = value;
  }

  onTeachingTypeChange(value: Record<string, unknown> | null): void {
    if (!this.hasSelectionChanged(this.selectedTeachingType, value)) {
      return;
    }
    this.selectedTeachingType = value;
    this.selectedDepartment = null;
    this.selectedDesignation = null;
    this.departmentSearch.setValue('');
    this.designationSearch.setValue('');
    this.departmentOptionsList = [];
    this.designationOptionsList = [];
    this.listsVersion$.next(this.listsVersion$.value + 1);

    const typeId = this.readId(value, ['typeId', 'teachTypeId', 'id']);
    if (typeId == null) {
      return;
    }

    this.teachingTypeLoading = true;
    this.beginLoading();
    this.subs.add(
      this.hrHttp
        .getMrfTypeWiseDesigAndDepartments(typeId)
        .pipe(
          catchError(() => of([])),
          finalize(() => {
            this.teachingTypeLoading = false;
            this.endLoading();
          })
        )
        .subscribe((res) => {
          const data = (res as { data?: unknown })?.data ?? res;
          this.departmentOptionsList = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
          this.listsVersion$.next(this.listsVersion$.value + 1);
        })
    );
  }

  onDepartmentChange(value: Record<string, unknown> | null): void {
    if (!this.hasSelectionChanged(this.selectedDepartment, value)) {
      return;
    }
    this.selectedDepartment = value;
    this.selectedDesignation = null;
    this.designationSearch.setValue('');
    this.designationOptionsList = Array.isArray(value?.['designations'])
      ? (value?.['designations'] as Record<string, unknown>[])
      : Array.isArray(value?.['desigDetails'])
        ? (value?.['desigDetails'] as Record<string, unknown>[])
        : [];
    this.listsVersion$.next(this.listsVersion$.value + 1);
  }

  onCampusChange(value: Record<string, unknown> | null): void {
    if (!this.hasSelectionChanged(this.selectedCampus, value, this.compareByCampus)) {
      return;
    }
    this.selectedCampus = value;
    this.clearLocationDependents('campus');
    this.reloadDownstreamLocationFields('campus');
  }

  onLocationSelectOpened(opened: boolean, field: BudgetLocationFilterField): void {
    if (!opened || this.isLocationFieldLoading(field)) {
      return;
    }
    if (this.getLocationOptionsList(field).length > 0) {
      return;
    }
    this.loadCustomizeOptions(field);
  }

  compareByCity = (
    a: Record<string, unknown> | null,
    b: Record<string, unknown> | null
  ): boolean => this.sameFilterRecord(a, b, ['cityId'], ['cityName', 'name']);

  compareByZone = (
    a: Record<string, unknown> | null,
    b: Record<string, unknown> | null
  ): boolean => this.sameFilterRecord(a, b, ['zoneId'], ['zoneName', 'zone', 'name']);

  compareBySubZone = (
    a: Record<string, unknown> | null,
    b: Record<string, unknown> | null
  ): boolean => this.sameFilterRecord(a, b, ['subZoneId'], ['subZone', 'subZoneName']);

  compareByCampus = (
    a: Record<string, unknown> | null,
    b: Record<string, unknown> | null
  ): boolean => this.sameFilterRecord(a, b, ['campusId'], ['campusName', 'name', 'campus']);

  compareByDean = (
    a: Record<string, unknown> | null,
    b: Record<string, unknown> | null
  ): boolean =>
    this.sameFilterRecord(a, b, ['deanId'], ['deanName', 'principalDeanName', 'dean', 'name']);

  compareById = (a: Record<string, unknown> | null, b: Record<string, unknown> | null): boolean => {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    const idKeys = [
      ['typeId', 'teachTypeId'],
      ['deptId', 'departmentId'],
      ['desigId', 'designationId'],
      ['empTypeId'],
      ['budgetId'],
      ['id'],
    ];
    for (const keys of idKeys) {
      const left = this.readId(a, keys);
      const right = this.readId(b, keys);
      if (left != null && right != null) {
        return left === right;
      }
    }
    const aName = this.readName(a, ['name', 'empType', 'departmentName', 'desigName']);
    const bName = this.readName(b, ['name', 'empType', 'departmentName', 'desigName']);
    return !!aName && aName === bName;
  };

  private sameFilterRecord(
    a: Record<string, unknown> | null,
    b: Record<string, unknown> | null,
    idKeys: string[],
    nameKeys: string[]
  ): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    const aId = this.readId(a, idKeys);
    const bId = this.readId(b, idKeys);
    if (aId != null && bId != null) {
      return aId === bId;
    }
    const aName = this.readName(a, nameKeys);
    const bName = this.readName(b, nameKeys);
    return !!aName && aName === bName;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private loadInitialLocationFilters(): void {
    this.loadCustomizeOptionsBatch([
      'city',
      'zone',
      'subZone',
      'campus',
      'deanName',
    ]);
  }

  private reloadDownstreamLocationFields(changedField: BudgetLocationFilterField): void {
    const downstreamFields = this.getLocationFieldsAfter(changedField);
    if (downstreamFields.length) {
      this.loadCustomizeOptionsBatch(downstreamFields);
    }
  }

  private getLocationFieldsAfter(field: BudgetLocationFilterField): BudgetLocationFilterField[] {
    const index = LOCATION_FILTER_ORDER.indexOf(field);
    if (index < 0) {
      return [];
    }
    return LOCATION_FILTER_ORDER.slice(index + 1);
  }

  private loadCustomizeOptionsBatch(
    targets: BudgetLocationFilterField[],
    onComplete?: () => void
  ): void {
    if (!targets.length) {
      return;
    }

    targets.forEach((target) => {
      this.locationFieldLoading[target] = true;
    });
    this.beginLoading();

    const requests: Record<string, Observable<Record<string, unknown>[]>> = {};
    targets.forEach((target) => {
      requests[target] = this.hrHttp
        .getMrfCustomizeFilterOptions(this.buildParentFilters(target), target)
        .pipe(catchError(() => of([])));
    });

    this.subs.add(
      forkJoin(requests)
        .pipe(
          finalize(() => {
            targets.forEach((target) => {
              this.locationFieldLoading[target] = false;
            });
            this.endLoading();
          })
        )
        .subscribe((results) => {
          targets.forEach((target) => {
            this.setLocationOptionsList(target, (results[target] ?? []) as Record<string, unknown>[]);
          });
          this.listsVersion$.next(this.listsVersion$.value + 1);
          onComplete?.();
        })
    );
  }

  private loadCustomizeOptions(target: BudgetLocationFilterField): void {
    const filters = this.buildParentFilters(target);
    this.locationFieldLoading[target] = true;
    this.beginLoading();
    this.subs.add(
      this.hrHttp
        .getMrfCustomizeFilterOptions(filters, target)
        .pipe(
          catchError(() => of([])),
          finalize(() => {
            this.locationFieldLoading[target] = false;
            this.endLoading();
          })
        )
        .subscribe((list) => {
          this.setLocationOptionsList(target, list as Record<string, unknown>[]);
          this.listsVersion$.next(this.listsVersion$.value + 1);
        })
    );
  }

  private buildParentFilters(exclude?: BudgetLocationFilterField): MrfLocationFilters {
    const filters: MrfLocationFilters = {};

    if (exclude !== 'city' && this.selectedCity) {
      const cityId = this.readId(this.selectedCity, ['cityId', 'id']);
      if (cityId != null) {
        filters.cityId = cityId;
      }
    }

    if (exclude !== 'zone' && this.selectedZone) {
      const zoneName = this.readName(this.selectedZone, ['zoneName', 'name', 'zone']);
      if (zoneName) {
        filters.zoneName = zoneName;
      }
    }

    if (exclude !== 'subZone' && this.selectedSubZone) {
      const subZone = this.readName(this.selectedSubZone, ['subZone', 'name', 'subZoneName']);
      if (subZone) {
        filters.subZone = subZone;
      }
    }

    if (exclude !== 'campus' && this.selectedCampus) {
      const campusId = this.readId(this.selectedCampus, ['campusId', 'id']);
      if (campusId != null) {
        filters.campusId = campusId;
      }
    }

    if (exclude !== 'deanName' && this.selectedDean) {
      const deanName = this.readName(this.selectedDean, [
        'deanName',
        'name',
        'dean',
        'principalDeanName',
      ]);
      if (deanName) {
        filters.deanName = deanName;
      }
    }

    return filters;
  }

  private setLocationOptionsList(
    target: BudgetLocationFilterField,
    list: Record<string, unknown>[]
  ): void {
    switch (target) {
      case 'city':
        this.cityOptionsList = list;
        break;
      case 'zone':
        this.zoneOptionsList = list;
        break;
      case 'subZone':
        this.subZoneOptionsList = list;
        break;
      case 'campus':
        this.campusOptionsList = list;
        break;
      case 'deanName':
        this.deanOptionsList = list;
        break;
    }
  }

  private getLocationOptionsList(field: BudgetLocationFilterField): Record<string, unknown>[] {
    switch (field) {
      case 'city':
        return this.cityOptionsList;
      case 'zone':
        return this.zoneOptionsList;
      case 'subZone':
        return this.subZoneOptionsList;
      case 'campus':
        return this.campusOptionsList;
      case 'deanName':
        return this.deanOptionsList;
    }
  }

  private clearLocationDependents(changedField: BudgetLocationFilterField): void {
    const downstreamFields = this.getLocationFieldsAfter(changedField);

    if (changedField === 'city') {
      this.selectedZone = null;
      this.selectedSubZone = null;
      this.selectedCampus = null;
      this.selectedDean = null;
      this.zoneSearch.setValue('');
      this.subZoneSearch.setValue('');
      this.campusSearch.setValue('');
      this.deanSearch.setValue('');
    } else if (changedField === 'zone') {
      this.selectedSubZone = null;
      this.selectedCampus = null;
      this.selectedDean = null;
      this.subZoneSearch.setValue('');
      this.campusSearch.setValue('');
      this.deanSearch.setValue('');
    } else if (changedField === 'subZone') {
      this.selectedCampus = null;
      this.selectedDean = null;
      this.campusSearch.setValue('');
      this.deanSearch.setValue('');
    } else if (changedField === 'campus') {
      this.selectedDean = null;
      this.deanSearch.setValue('');
    }

    downstreamFields.forEach((field) => {
      this.setLocationOptionsList(field, []);
      this.locationFieldLoading[field] = false;
    });

    this.listsVersion$.next(this.listsVersion$.value + 1);
  }

  private loadBudgetList(filters: MrfLocationFilters): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.beginLoading();
    this.subs.add(
      forkJoin({
        res: this.hrHttp.getCampusBudgetReportWithFilters(filters).pipe(
          catchError((err) => {
            console.error('getCampusBudgetReportWithFilters failed', err);
            this.hrAlert.show('Unable to load budget report.', 'Error');
            return of(null);
          })
        ),
        empTypes: this.hrHttp.getMrfBasicInfo().pipe(catchError(() => of(null))),
        teachTypes: this.hrHttp.getMrfTeachTypes().pipe(catchError(() => of([]))),
      })
        .pipe(
          finalize(() => {
            this.loading = false;
            this.endLoading();
          })
        )
        .subscribe(({ res, empTypes, teachTypes }) => {
          const parsed = this.hrHttp.parseBudgetReportResponse(res);
          this.rowData = this.hrHttp.applyBudgetReportTypeLabels(parsed.rows, empTypes, teachTypes);
          this.totalBudget = parsed.totalBudget;
          this.quickFilter = '';
          this.listLoaded = true;
          this.syncDisplayedRows();
        })
    );
  }

  private syncDisplayedRows(): void {
    const query = this.quickFilter.trim().toLowerCase();
    if (!query) {
      this.displayRowData = [...this.rowData];
      return;
    }
    this.displayRowData = this.rowData.filter((row) =>
      [
        row.city,
        row.zone,
        row.subZone,
        row.campus,
        row.deanName,
        row.empType,
        row.teachingType,
        row.department,
        row.designation,
        row.subjectName,
        this.formatBudget(row.budgetAmount),
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }

  private loadExpandedFilterMasters(onComplete?: () => void): void {
    if (this.expandedFiltersLoaded) {
      onComplete?.();
      return;
    }
    this.expandedFiltersLoaded = true;
    this.expandedFiltersLoading = true;
    this.beginLoading();
    this.subs.add(
      forkJoin({
        teachTypes: this.hrHttp.getMrfTeachTypes().pipe(catchError(() => of([]))),
        basicInfo: this.hrHttp.getMrfBasicInfo().pipe(catchError(() => of(null))),
      })
        .pipe(
          finalize(() => {
            this.expandedFiltersLoading = false;
            this.endLoading();
          })
        )
        .subscribe(({ teachTypes, basicInfo }) => {
          this.teachingTypeOptionsList = this.hrHttp.parseMrfTeachTypesResponse(teachTypes);
          const basic = (basicInfo as { data?: Record<string, unknown> })?.data ?? basicInfo ?? {};
          const empTypes = (basic as Record<string, unknown>)['empTypes'] ??
            (basic as Record<string, unknown>)['employeeTypes'] ??
            [];
          this.empTypeOptionsList = (Array.isArray(empTypes) ? empTypes : []).map((item) => {
            const row = item as Record<string, unknown>;
            const label = String(row['empType'] ?? row['employeeType'] ?? row['name'] ?? '').trim();
            return { ...row, label, name: label };
          }).filter((item) => !!item.label);
          this.listsVersion$.next(this.listsVersion$.value + 1);
          onComplete?.();
        })
    );
  }

  private beginLoading(): void {
    this.pendingApiCount += 1;
    if (this.pendingApiCount === 1) {
      void this.spinner.show();
    }
  }

  private endLoading(): void {
    this.pendingApiCount = Math.max(0, this.pendingApiCount - 1);
    if (this.pendingApiCount === 0) {
      void this.spinner.hide();
    }
  }

  private initFilteredOptions(): void {
    this.subs.add(
      combineLatest([
        this.citySearch.valueChanges.pipe(startWith('')),
        this.zoneSearch.valueChanges.pipe(startWith('')),
        this.subZoneSearch.valueChanges.pipe(startWith('')),
        this.campusSearch.valueChanges.pipe(startWith('')),
        this.deanSearch.valueChanges.pipe(startWith('')),
        this.empTypeSearch.valueChanges.pipe(startWith('')),
        this.teachingTypeSearch.valueChanges.pipe(startWith('')),
        this.departmentSearch.valueChanges.pipe(startWith('')),
        this.designationSearch.valueChanges.pipe(startWith('')),
        this.budgetSearch.valueChanges.pipe(startWith('')),
        this.listsVersion$,
      ]).subscribe(([cityQ, zoneQ, subZoneQ, campusQ, deanQ, empQ, teachQ, deptQ, desigQ, budgetQ]) => {
        this.cityOptions = of(this.filterList(this.cityOptionsList, cityQ, ['name', 'cityName']));
        this.zoneOptions = of(this.filterList(this.zoneOptionsList, zoneQ, ['zoneName', 'name', 'zone']));
        this.subZoneOptions = of(
          this.filterList(this.subZoneOptionsList, subZoneQ, ['subZone', 'name', 'subZoneName'])
        );
        this.campusOptions = of(
          this.filterList(this.campusOptionsList, campusQ, ['campusName', 'name', 'campus'])
        );
        this.deanOptions = of(
          this.filterList(this.deanOptionsList, deanQ, [
            'deanName',
            'principalDeanName',
            'name',
            'dean',
          ])
        );
        this.empTypeOptions = of(
          this.filterList(this.empTypeOptionsList, empQ, ['label', 'name', 'empType', 'employeeType'])
        );
        this.teachingTypeOptions = of(
          this.filterList(this.teachingTypeOptionsList, teachQ, ['roleType', 'TeachType', 'name'])
        );
        this.departmentOptions = of(
          this.filterList(this.departmentOptionsList, deptQ, ['deptName', 'departmentName', 'name'])
        );
        this.designationOptions = of(
          this.filterList(this.designationOptionsList, desigQ, ['desigName', 'designation', 'name'])
        );
        this.budgetOptions = of(
          this.filterList(this.budgetOptionsList, budgetQ, ['label', 'value'])
        );
      })
    );
  }

  private hasSelectionChanged(
    previous: Record<string, unknown> | null,
    next: Record<string, unknown> | null,
    compare: (
      a: Record<string, unknown> | null,
      b: Record<string, unknown> | null
    ) => boolean = this.compareById
  ): boolean {
    if (previous === next) {
      return false;
    }
    if (!previous && !next) {
      return false;
    }
    if (!previous || !next) {
      return true;
    }
    return !compare(previous, next);
  }

  private filterList(
    list: Record<string, unknown>[],
    query: string | null | undefined,
    keys: string[]
  ): Record<string, unknown>[] {
    const q = String(query ?? '')
      .trim()
      .toLowerCase();
    if (!q) {
      return list;
    }
    return list.filter((item) =>
      keys.some((key) =>
        String(item?.[key] ?? '')
          .toLowerCase()
          .includes(q)
      )
    );
  }

  private bindSelectPanelSync(): void {
    this.selectPanelSubs.unsubscribe();
    this.selectPanelSubs = new Subscription();
    this.matSelects?.forEach((select) => {
      this.selectPanelSubs.add(
        select.openedChange.subscribe((opened) => {
          if (opened) {
            this.syncSelectPanelWidth(select);
          }
        })
      );
    });
  }

  private syncSelectPanelWidth(select: MatSelect): void {
    requestAnimationFrame(() => {
      const triggerWidth = select._elementRef.nativeElement.getBoundingClientRect().width;
      const panelEl = select.panel?.nativeElement;
      if (!panelEl || !triggerWidth) {
        return;
      }
      panelEl.style.minWidth = `${triggerWidth}px`;
      panelEl.style.width = `${triggerWidth}px`;
      panelEl.style.maxWidth = `${triggerWidth}px`;
    });
  }

  private readId(
    item: Record<string, unknown> | null | undefined,
    keys: string[]
  ): number | null {
    if (!item) {
      return null;
    }
    for (const key of keys) {
      const value = item[key];
      if (value == null || value === '') {
        continue;
      }
      const num = Number(value);
      if (Number.isFinite(num)) {
        return num;
      }
    }
    return null;
  }

  private readName(
    item: Record<string, unknown> | null | undefined,
    keys: string[]
  ): string {
    if (!item) {
      return '';
    }
    for (const key of keys) {
      const value = item[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }

  private formatBudget(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '—';
    }
    return `₹ ${this.hrHttp.formatMrfBudgetAmount(value)}`;
  }

  private canEditBudgetRow(row: BudgetReportRow | undefined): boolean {
    return (
      BUDGET_FORM_ENABLED &&
      !!row &&
      this.hasBudgetAccess &&
      this.hrHttp.isCampusBudgetAmountEditable(row)
    );
  }

  private buildEditCell(row?: BudgetReportRow): string {
    const enabled = this.canEditBudgetRow(row);
    const disabledAttr = enabled ? '' : ' disabled';
    const disabledClass = enabled ? '' : ' brpt__edit-btn--disabled';
    return `<button type="button" class="brpt__edit-btn${disabledClass}"${disabledAttr}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 20H21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <path d="M16.5 3.5C16.8978 3.10217 17.4374 2.87868 18 2.87868C18.5626 2.87868 19.1022 3.10217 19.5 3.5C19.8978 3.89783 20.1213 4.43739 20.1213 5C20.1213 5.56261 19.8978 6.10217 19.5 6.5L7 19L3 20L4 16L16.5 3.5Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Update
    </button>`;
  }

  private onEditRow(row: BudgetReportRow | undefined): void {
    if (!row || !BUDGET_FORM_ENABLED) {
      return;
    }
    if (!this.hasBudgetAccess) {
      this.hrAlert.show('No permission to edit budget.', 'Warning');
      return;
    }
    if (!this.hrHttp.isCampusBudgetAmountEditable(row)) {
      return;
    }
    this.persistFilterState();
    void this.router.navigate(['/hrDetails/budgetDetails'], {
      state: { budgetEditRow: row },
    });
  }

  private persistFilterState(listLoaded = this.listLoaded): void {
    try {
      const locationFilters = this.buildCurrentLocationFilters();
      const state: BudgetReportPersistedState = {
        selectedCity: this.selectedCity,
        selectedZone: this.selectedZone,
        selectedSubZone: this.selectedSubZone,
        selectedCampus: this.selectedCampus,
        selectedDean: this.selectedDean,
        selectedEmpType: this.selectedEmpType,
        selectedTeachingType: this.selectedTeachingType,
        selectedDepartment: this.selectedDepartment,
        selectedDesignation: this.selectedDesignation,
        selectedBudget: this.selectedBudget,
        showMoreFilters: this.showMoreFilters,
        locationFilters,
        listLoaded,
      };
      sessionStorage.setItem(BUDGET_REPORT_FILTER_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }

  private buildCurrentLocationFilters(): MrfLocationFilters {
    const campusId = this.readId(this.selectedCampus, ['campusId', 'id']);
    if (campusId == null) {
      return { ...this.locationFilters };
    }

    return {
      cityId: this.readId(this.selectedCity, ['cityId', 'id']),
      zoneName: this.readName(this.selectedZone, ['zoneName', 'name', 'zone']),
      subZone: this.readName(this.selectedSubZone, ['subZone', 'name', 'subZoneName']),
      campusId,
      deanName: this.readName(this.selectedDean, ['deanName', 'name', 'dean']),
    };
  }

  private readPersistedFilterState(): BudgetReportPersistedState | null {
    try {
      const raw = sessionStorage.getItem(BUDGET_REPORT_FILTER_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as BudgetReportPersistedState;
    } catch {
      return null;
    }
  }

  private restoreFilterState(state: BudgetReportPersistedState): void {
    this.selectedCity = state.selectedCity ?? null;
    this.selectedZone = state.selectedZone ?? null;
    this.selectedSubZone = state.selectedSubZone ?? null;
    this.selectedCampus = state.selectedCampus ?? null;
    this.selectedDean = state.selectedDean ?? null;
    this.selectedEmpType = state.selectedEmpType ?? null;
    this.selectedTeachingType = state.selectedTeachingType ?? null;
    this.selectedDepartment = state.selectedDepartment ?? null;
    this.selectedDesignation = state.selectedDesignation ?? null;
    this.selectedBudget = state.selectedBudget ?? null;
    this.showMoreFilters = !!state.showMoreFilters;
    this.locationFilters = { ...(state.locationFilters ?? {}) };
    this.listsVersion$.next(this.listsVersion$.value + 1);

    this.loadCustomizeOptionsBatch(LOCATION_FILTER_ORDER, () => {
      this.finishFilterStateRestore(state);
    });
  }

  private finishFilterStateRestore(state: BudgetReportPersistedState): void {
    if (this.showMoreFilters) {
      this.expandedFiltersLoaded = false;
      this.loadExpandedFilterMasters(() => {
        this.restoreExpandedFilterSelections(() => {
          this.reloadBudgetListIfNeeded(state);
        });
      });
      return;
    }

    this.reloadBudgetListIfNeeded(state);
  }

  private reloadBudgetListIfNeeded(state: BudgetReportPersistedState): void {
    const campusId = state.locationFilters?.campusId ?? this.readId(this.selectedCampus, ['campusId', 'id']);
    if (state.listLoaded && campusId != null) {
      this.loadBudgetList(state.locationFilters);
    }
  }

  private restoreExpandedFilterSelections(onComplete?: () => void): void {
    const typeId = this.readId(this.selectedTeachingType, ['typeId', 'teachTypeId', 'id']);
    if (typeId == null) {
      onComplete?.();
      return;
    }

    this.teachingTypeLoading = true;
    this.beginLoading();
    this.subs.add(
      this.hrHttp
        .getMrfTypeWiseDesigAndDepartments(typeId)
        .pipe(
          catchError(() => of([])),
          finalize(() => {
            this.teachingTypeLoading = false;
            this.endLoading();
          })
        )
        .subscribe((res) => {
          const data = (res as { data?: unknown })?.data ?? res;
          this.departmentOptionsList = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];

          if (this.selectedDepartment) {
            this.designationOptionsList = Array.isArray(this.selectedDepartment['designations'])
              ? (this.selectedDepartment['designations'] as Record<string, unknown>[])
              : Array.isArray(this.selectedDepartment['desigDetails'])
                ? (this.selectedDepartment['desigDetails'] as Record<string, unknown>[])
                : [];
          }

          this.listsVersion$.next(this.listsVersion$.value + 1);
          onComplete?.();
        })
    );
  }
}
